import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";

import NumberInput from "@/components/form/number-input";
import {
  CATEGORIES,
  CATEGORY_ALL,
  CATEGORY_ESCAPE_ROOM,
} from "@/components/game-categories/constants";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";
import { VALID_FILTERS } from "../hooks/use-search-filters";
import useSelectedCategories from "../hooks/use-selected-categories";
import {
  DifficultyEnum,
  GameStatusFilterEnum,
  type IncludeEnum,
  PrivacyEnum,
} from "../types";
import CategoryFilter from "./category-filter";
import CheckboxFilter from "./checkbox-filter";
import HalloweenFilter from "./halloween-filter";
import { CouponsIcon } from "./icons";
import { ICONS as INCLUDE_ICONS } from "./include-filter";
import RadioFilter from "./radio-filter";

const MOBILE_DIVIDER_CLASSNAME =
  "pb-5 md:pb-0 border-white/8 border-b md:border-0";

function EscapeRoomFilters() {
  return (
    <>
      <div className={MOBILE_DIVIDER_CLASSNAME}>
        <RadioFilter
          label="Duration"
          name="duration"
          options={[
            ["-60", "Less than 60 minutes"],
            ["60", "60 minutes"],
            ["60+", "More than 60 minutes"],
            ["120+", "2 to 4 hours"],
            ["240+", "More than 4 hours"],
          ]}
        />
      </div>
      <div className={MOBILE_DIVIDER_CLASSNAME}>
        <CheckboxFilter
          label="Difficulty"
          name="difficulty"
          options={[
            [DifficultyEnum.EASY, "Easy"],
            [DifficultyEnum.MEDIUM, "Medium"],
            [DifficultyEnum.HARD, "Hard"],
            [DifficultyEnum.EXPERT, "Expert"],
            [DifficultyEnum.UNKNOWN, "Unrated"],
          ]}
        />
      </div>
      <div className={MOBILE_DIVIDER_CLASSNAME}>
        <CheckboxFilter
          label="Privacy"
          name="privacy"
          options={[
            [PrivacyEnum.ALWAYS_PRIVATE, "Always private"],
            [PrivacyEnum.SOMETIMES_PRIVATE, "Sometimes private"],
            [PrivacyEnum.NEVER_PRIVATE, "Never private"],
          ]}
        />
      </div>
    </>
  );
}

interface FiltersProps {
  onChange?: () => void;
}

export default function Filters({ onChange }: FiltersProps) {
  const { includeLabels } = useSelectedCategories();

  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const { includeLabel, primaryCategories } = useSelectedCategories();
  const { isReady: splitReady, treatments } = useSplitTreatments({
    names: ["halloween_party"],
  });

  const getFiltersStringValue = (field: string, defaultValue = ""): string => {
    const value = search[field as keyof ExploreSearchParams];
    if (!value) {
      return defaultValue;
    }
    if (typeof value === "string") {
      return value;
    }
    return String(value);
  };

  const splitContent = (treatments: SplitIO.TreatmentsWithConfig) => {
    const features: React.ReactNode[] = [];
    if (
      "halloween_party" in treatments &&
      treatments.halloween_party.treatment === "on"
    ) {
      features.push(
        <div className={MOBILE_DIVIDER_CLASSNAME} key="halloween_party">
          <HalloweenFilter />
        </div>
      );
    }
    return features;
  };

  const extraFields = useMemo(() => {
    const fieldsBlackList = [...VALID_FILTERS, "params", "include"];

    return Object.entries(search).reduce((accum, [field, value]) => {
      if (!value) {
        return accum;
      }
      if (fieldsBlackList.includes(field)) {
        return accum;
      }
      if (typeof value === "string") {
        accum.push(
          <Input key={field} name={field} type="hidden" value={value} />
        );
      } else {
        accum.push(
          <Input key={field} name={field} type="hidden" value={String(value)} />
        );
      }
      return accum;
    }, [] as React.ReactNode[]);
  }, [search]);

  const categoryFilters = useMemo(() => {
    if (primaryCategories.includes(CATEGORY_ESCAPE_ROOM)) {
      return <EscapeRoomFilters />;
    }

    return null;
  }, [primaryCategories]);

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-1 gap-5 md:gap-8",
          primaryCategories.length ? "md:grid-cols-3" : "md:grid-cols-2"
        )}
      >
        {!primaryCategories.length && (
          <div className={MOBILE_DIVIDER_CLASSNAME}>
            <RadioFilter
              label={
                <>
                  Categories{" "}
                  <p className="mt-2 whitespace-nowrap rounded-[5px] bg-linear-91 from-mystic-red/10 to-electric-purple px-2.5 py-1 text-center font-bold text-14">
                    ⭐️ Pick a category to see more filters ⭐
                  </p>
                </>
              }
              name="categories"
              options={Object.entries(CATEGORIES).map(
                ([key, { Icon, label }]) =>
                  [
                    key === CATEGORY_ALL ? "" : key,
                    <>
                      <Icon className="inline text-white" />
                      <span className="ml-2.5">{label}</span>
                    </>,
                  ] as [string, React.ReactNode]
              )}
              selected=""
            />
          </div>
        )}
        <div className={cn(MOBILE_DIVIDER_CLASSNAME, "md:hidden")}>
          <RadioFilter
            label={includeLabel}
            name="include"
            options={Object.entries(includeLabels).map(([key, value]) => {
              const Icon = INCLUDE_ICONS[key as IncludeEnum];

              return [
                key,
                <>
                  <Icon className="inline" />
                  <span className="ml-2.5">{value}</span>
                </>,
              ] as [string, React.ReactNode];
            })}
          />
        </div>
        {splitReady && splitContent(treatments)}
        <CategoryFilter
          divider={<div className={MOBILE_DIVIDER_CLASSNAME} />}
        />
        <div className={MOBILE_DIVIDER_CLASSNAME}>
          <div className="flex items-center justify-between md:flex-col md:items-start md:justify-start">
            <h5 className="font-medium text-16 leading-[1.5em] md:mb-2.5 md:leading-[1.2em]">
              Group size
            </h5>
            <NumberInput
              inputProps={{
                defaultValue: Number.parseInt(
                  getFiltersStringValue("players", "0"),
                  10
                ).toString(),
                inputMode: "decimal",
                min: 0,
                name: "players",
                onChange,
                pattern: "[0-9]*",
              }}
            />
          </div>
        </div>
        <div className={MOBILE_DIVIDER_CLASSNAME}>
          <CheckboxFilter
            label={
              <>
                Discounts <CouponsIcon className="ml-1 inline" />
              </>
            }
            name="coupons"
            options={[["true", "Show only attractions with discounts"]]}
          />
        </div>
        {categoryFilters}
        {/* <Box
      alignItems="center"
      display={['flex', null, null, 'block']}
      justifyContent="space-between"
    >
      <Heading
        as="h5"
        fontSize={[17, null, null, 18]}
        lineHeight="1.2em"
        mb={[null, null, null, 4]}
      >
        Minimum age
      </Heading>
      <NumberInput
        inputProps={{
          defaultValue: Number.parseInt(getFiltersStringValue('age', '0')).toString(),
          name: 'age',
        }}
      />
    </Box> */}
        {/* <CheckboxFilter
      options={['Has a live actor', 'Not scary', 'Good for kids', 'Safe for work']}
      name="details"
      title="Room details"
    /> */}
        <div>
          <CheckboxFilter
            label="Closed attractions"
            name="status"
            options={[
              [
                GameStatusFilterEnum.PERMANENTLY_CLOSED,
                "Show closed attractions",
              ],
            ]}
          />
        </div>
      </div>
      {extraFields}
    </>
  );
}
